import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import ajaxService from "services/ajaxService";
import { useMyHubsList } from "hooks/user-profile/useUserProfile";

export const useShippingUnitList = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(
    ["shipping-unit", "list"],
    () => ajaxService.getShippingUnitList(),
    {
      enabled: true,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        return response;
      },
    }
  );
};

// Locations By Customer
export const useLocationsByCustomer = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  const { customerId } = payload;

  return useQuery(
    ["locations-customer", customerId],
    () => ajaxService.getLocationsByCustomer(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      // initialData: [],
      select: response => {
        const { data } = response;
        data.sort((a, b) => {
          // Sort by client_hub_id in descending order
          const clientHubComparison = b.client_hub_id - a.client_hub_id;

          // If client_hub_id is the same, then sort by checker_date in descending order
          return clientHubComparison !== 0
            ? clientHubComparison
            : b.checker_date - a.checker_date;
        });
        return {
          items: data || [],
        };
      },
    }
  );
};

export const useProvinces = (payload, onSuccess, onError, enabled) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["provinces", payload], () => ajaxService.getAjax(payload), {
    enabled: enabled || false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    // initialData: Object({ data: [] }),
    select: response => {
      return response;
    },
  });
};

export const useDistricts = (payload, onSuccess, onError, enabled) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["districts", payload], () => ajaxService.getAjax(payload), {
    enabled: enabled || false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    // initialData: Object({ null }),
    select: response => {
      return response;
    },
  });
};

export const useFreeWard = (payload, onSuccess, onError, enabled) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();
  const { data: myHubs } = useMyHubsList({ enabled: true });
  const centerId = myHubs?.hub?.id || null;
  return useQuery(
    ["list-free-ward", payload],
    () =>
      ajaxService.getListFreeWard({
        ...payload,
        center_id: centerId,
      }),
    {
      enabled: enabled || false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        return response;
      },
    }
  );
};

export const useSales = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["sales", payload], () => ajaxService.getListSale(payload), {
    enabled: false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      return response;
    },
  });
};

export const useWards = (payload, onSuccess, onError, enabled) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["wards", payload], () => ajaxService.getAjax(payload), {
    enabled: enabled || false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    // initialData: Object({ data: [] }),
    select: response => {
      return response;
    },
  });
};

export const useShippingUnits = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["shipping-units"], () => ajaxService.shippingUnits(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      const data = Object.keys(response).map(key => {
        return { value: key, label: response[key] };
      });

      if (typeof response === "object") {
        return data;
      }
      return response;
    },
  });
};

/**
 * List Locality
 * @param {MutationProps<{
 *  items: DefaultObjectSelect[];
 *  variables: {
 *    countryId: number;
 *    type: number;
 *    parentCode: string;
 *  };
 * }>} val
 */
export const useListLocality = ({ onSuccess, onError }) => {
  const { t } = useTranslation(["common"]);
  const { addToast } = useToasts();

  return useMutation({
    mutationFn: ajaxService.listLocality,
    retry: 1,
    onSuccess: (response, variables) => {
      /**
       * @type {{
       *  data?: Record<string, any>
       * }}
       */
      const { data } = response;

      /** @type {DefaultObjectSelect[]} */
      let items = [];

      if (data && typeof data === "object") {
        items = Object.entries(data).map(([key, value]) => ({
          label: value,
          value: key,
        }));
      }

      if (typeof onSuccess === "function") {
        onSuccess({ items, variables });
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.list", {
              target: t("locality"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useShippingUnitsAll = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(
    ["shipping-units-all"],
    () => ajaxService.shippingUnitsAll(),
    {
      enabled: true,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const data = Object.keys(response).map(key => {
          return { value: key, label: response[key] };
        });

        if (typeof response === "object") {
          return data;
        }
        return response;
      },
    }
  );
};

export const useResourceType = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["resource-type"], () => ajaxService.getResourceType(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_resourceType", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object(),
    select: response => {
      return response;
    },
  });
};

export const useUserType = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(["user-type"], () => ajaxService.getUserType(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_userType", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object(),
    select: response => {
      return response;
    },
  });
};
export const useRunnerByRule = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  const { time_slot, action_date } = payload;
  return useQuery(
    ["runner-by-rule", time_slot, action_date],
    () => ajaxService.getRunnerByRule(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess: response => {
        const { result } = response;
        if (result) {
          if (typeof onSuccess === "function") {
            onSuccess(result);
          }
        }
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_userType", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object(),
      select: response => {
        return response;
      },
    }
  );
};

// Locations By Customer
export const useLocationsCustomerByRule = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  const { customerId = null } = payload;

  return useQuery(
    ["locations-customer", customerId],
    () => ajaxService.getLocationsCustomerByRule(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      // initialData: [],
      select: response => {
        const { data } = response;

        return {
          items: data || [],
        };
      },
    }
  );
};
// Locations By Customer
export const useCustomerShippingType = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  const { customerId } = payload;

  return useQuery(
    ["customer-shipping-type", customerId],
    () => ajaxService.getCustomerShippingType(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        const resData = Object.keys(data)?.map(key => {
          return { value: data[key], label: data[key]?.name };
        });

        if (typeof response === "object") {
          return resData;
        }

        return response;
      },
    }
  );
};

// useGetCustomerLocationTransports
export const useGetCustomerLocationTransports = (
  payload,
  onSuccess,
  onError
) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  const { customerId } = payload;

  return useQuery(
    ["CustomerLocationTransports", customerId],
    () => ajaxService.getCustomerLocationTransports(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

export const useChangePassword = (onSuccess, onError) => {
  const { t } = useTranslation("translation");
  const { addToast } = useToasts();

  return useMutation(ajaxService.changePassword, {
    retry: 0,
    onSuccess: data => {
      if (data?.data?.result) {
        addToast("Đổi mật khẩu thành công", {
          appearance: "success",
          autoDismiss: true,
        });
        setTimeout(() => {
          if (typeof onSuccess === "function") {
            onSuccess(data);
          }
        }, 500);
      } else {
        switch (data?.data.message) {
          case "ERRORS.PASSWORD_IS_NOT_CORRECT":
            addToast("Mật khẩu cũ không đúng", {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            addToast("Đổi mật khẩu thất bại", {
              appearance: "error",
              autoDismiss: true,
            });
            break;
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.change_password", {
              target: t("pa"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useGroupLocationList = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation();

  return useQuery(
    ["group-location", payload],
    () => ajaxService.getGroupLocationList(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const convertedArray = [];
        for (let key in response) {
          if (response.hasOwnProperty(key)) {
            convertedArray.push({
              value: key,
              label: response[key],
            });
          }
        }
        return convertedArray;
      },
    }
  );
};
